import { Table, Button, Space, DatePicker, Modal, Form, message } from 'antd';
import { DownloadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import api from '../utils/api';
import type { ArchiveRecord, PaginatedResponse } from '../types';
import dayjs from 'dayjs';

export default function Archives() {
  const [archives, setArchives] = useState<ArchiveRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const res = await api.get('archives').json<{ success: boolean; data: PaginatedResponse<ArchiveRecord> }>();
      if (res.success && res.data) {
        setArchives(res.data.items);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchives();
  }, []);

  const handleCreateArchive = async (values: { startTime: dayjs.Dayjs; endTime: dayjs.Dayjs }) => {
    try {
      await api.post('archives', {
        json: {
          startTime: values.startTime.toISOString(),
          endTime: values.endTime.toISOString(),
        },
      });
      message.success('归档任务已启动');
      setModalOpen(false);
      fetchArchives();
    } catch (error) {
      message.error('归档失败');
    }
  };

  const handleDownload = (id: number) => {
    window.open(`/api/archives/${id}/download`, '_blank');
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个归档文件吗？',
      onOk: async () => {
        await api.delete(`archives/${id}`);
        message.success('删除成功');
        fetchArchives();
      },
    });
  };

  const columns = [
    { title: '文件名', dataIndex: 'file_name', key: 'file_name' },
    { title: '日志数量', dataIndex: 'log_count', key: 'log_count' },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size: number) => `${(size / 1024 / 1024).toFixed(2)} MB`,
    },
    { title: '开始时间', dataIndex: 'start_time', key: 'start_time' },
    { title: '结束时间', dataIndex: 'end_time', key: 'end_time' },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at' },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: ArchiveRecord) => (
        <Space>
          <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownload(record.id)}>
            下载
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>日志归档</h2>
        <Button type="primary" icon={<EyeOutlined />} onClick={() => setModalOpen(true)}>
          创建归档
        </Button>
      </div>
      <Table columns={columns} dataSource={archives} rowKey="id" loading={loading} />
      <Modal
        title="创建归档"
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateArchive}>
          <Form.Item name="startTime" label="开始时间" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="endTime" label="结束时间" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
